"use client";

import {
    useEffect,
    useRef,
} from "react";
import Link from "next/link";
import MainMenu from "@/components/navigation/MainMenu";
import { useLanguage } from "@/components/LanguageContext";
import {
    getLocalizedPath,
} from "@/lib/localizedRoutes";

function TripadvisorWidget({
    language,
}) {
    const containerRef =
        useRef(null);

    useEffect(
        () => {
            const container =
                containerRef.current;

            if (
                !container
            ) {
                return;
            }

            container.innerHTML =
                "";

            const isEnglish =
                language ===
                "en";

            const uniq =
                isEnglish
                    ? "243"
                    : "663";

            const domain =
                isEnglish
                    ? "www.tripadvisor.com"
                    : "www.tripadvisor.co.hu";

            const lang =
                isEnglish
                    ? "en_US"
                    : "hu";

            const wrapper =
                document.createElement(
                    "div"
                );

            wrapper.id =
                `TA_selfserveprop${uniq}`;

            wrapper.className =
                "TA_selfserveprop";

            wrapper.innerHTML = `
                <ul class="TA_links">
                    <li>
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://${domain}/Attraction_Review-g274887-d34688509-Reviews-Strike-Budapest_Central_Hungary.html"
                        >
                            <img
                                src="https://${domain}/img/cdsi/img2/branding/v2/Tripadvisor_lockup_horizontal_secondary_registered-11900-2.svg"
                                alt="Tripadvisor"
                            />
                        </a>
                    </li>
                </ul>
            `;

            container.appendChild(
                wrapper
            );

            const script =
                document.createElement(
                    "script"
                );

            script.async =
                true;

            script.src =
                `https://www.jscache.com/wejs?wtype=selfserveprop&uniq=${uniq}&locationId=34688509&lang=${lang}&rating=true&nreviews=5&writereviewlink=true&popIdx=true&iswide=false&border=true&display_version=2`;

            script.setAttribute(
                "data-loadtrk",
                ""
            );

            script.onload =
                function () {
                    this.loadtrk =
                        true;
                };

            container.appendChild(
                script
            );

            return () => {
                container.innerHTML =
                    "";
            };
        },
        [
            language,
        ]
    );

    return (
        <div
            ref={containerRef}
            className="flex w-[240px] shrink-0 justify-center"
        />
    );
}

function GoogleReviewsWidget({
    language,
}) {
    const data = {
        rating:
            5.0,

        reviewCount:
            23,

        latestReview:
            language === "en"
                ? "I felt very good, I recommend it. "
                : "Nagyon jól éreztem magam,...",
    };

    const reviewsUrl =
        "https://www.google.com/search?sca_esv=8f55d1c39308862b&sxsrf=APpeQntdDmRIPissUwASu92QPERwIYYglQ:1789647102415&uds=AJ5uw1_a2D0D09lxm8gpKKOTUn4rKA2n816B-HmfEF_XXffC9TZFgBkp47jxzduipw0CsVrtcbGUMBtCfgU8e88Et-mR1xFiZqSj-Umyi-5o1SI1AslpRn0_EwisDZ-qG33TwJYN8VBwhr37AmqrK8sIxJfwgnnbnFdIFdebfWNqytGsPh9e4GZ_TlyE4loG21xNNLERePvt9wHbgyAGnP9xJjs65e2D-aDRXPIpMt4TsKF2-WMdUoWaEb5JfUEzlo8w5ni5H-ec&q=Strike+V%C3%A9lem%C3%A9nyek&si=APenkKm7iecQ4G6P-TsbSMFKIQtv3EFIqRAFw-i8uEbk55Z-_xLXm7erazUmBqAaaEy--4Sme32l2DmWRbqKk2UH4VPU8Hh6hnlbw2QZTIqAgnDnLGae3X9uIlXIS0oL6XyBX1DTXXQc&hl=hu-HU&sa=X&ved=2ahUKEwik17_-yvWWAxWccfEDHRfJI7EQ_4MLegQIOBAO&biw=1710&bih=992&dpr=2";

    const writeReviewUrl =
        "https://g.page/r/CU6GbK6U9zISEBM/review";

    return (
        <div className="
            h-[240px]
            w-[240px]
            shrink-0
            bg-[conic-gradient(from_45deg,#4285F4_0deg_90deg,#34A853_90deg_180deg,#FBBC05_180deg_270deg,#EA4335_270deg_360deg)]
            p-[2px]
        ">
            <div className="
                flex
                h-full
                w-full
                flex-col
                bg-white
                px-4
                py-4
                text-left
                text-black
            ">
                <div className="flex items-center">
                    <svg
                        viewBox="0 0 272 92"
                        className="h-[20px] w-auto"
                        aria-label="Google"
                    >
                        <text
                            x="0"
                            y="65"
                            fontSize="64"
                            fontWeight="600"
                            fontFamily="Arial, sans-serif"
                        >
                            Google
                        </text>
                    </svg>
                </div>

                <div className="mt-3">
                    <a
                        href="https://share.google/orgH5EV13twtcM85F"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="
        inline-block
        text-[22px]
        font-bold
        leading-none
        underline
        decoration-[2px]
        underline-offset-[3px]
        transition-opacity
        hover:opacity-70
    "
                    >
                        Strike
                    </a>

                    <div className="mt-3 flex items-center gap-2">
                        <span className="text-[18px] font-semibold leading-none">
                            {data.rating}
                        </span>

                        <div className="flex text-[15px] leading-none tracking-[1px] text-[#fbbc04]">
                            ★★★★★
                        </div>
                    </div>

                    <div className="mt-1 text-[11px] leading-[1.3] text-black/60">
                        {language === "en"
                            ? `${data.reviewCount} Google reviews`
                            : `${data.reviewCount} Google-értékelés`}
                    </div>
                </div>

                <div className="mt-3">
                    <div className="text-[11px] leading-[1.3] text-black/55">
                        {language === "en"
                            ? "Latest review:"
                            : "Legutóbbi értékelés:"}
                    </div>

                    <div className="mt-1 line-clamp-3 text-[13px] leading-[1.35] text-black/80">
                        “{data.latestReview}”
                    </div>
                </div>


                <div className="mt-auto pt-3">
                    <div className="flex items-center gap-2 text-[11px] leading-none">
                        <a
                            href={reviewsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2"
                        >
                            {language === "en"
                                ? "Read reviews"
                                : "Értékelések"}
                        </a>

                        <span className="text-black/30">
                            |
                        </span>

                        <a
                            href={writeReviewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2"
                        >
                            {language === "en"
                                ? "Write review"
                                : "Írjon értékelést"}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Footer() {
    const {
        language,
    } = useLanguage();

    return (
        <footer className="bg-black px-5 pb-16 pt-12 text-center text-white md:pb-20 md:pt-16">
            <div className="mx-auto flex max-w-[430px] flex-col items-center md:max-w-5xl">
                <Link
                    href={getLocalizedPath(
                        "/",
                        language
                    )}
                >
                    <img
                        src="/v4/hero-background.gif"
                        alt="Strike Urban Playground"
                        className="h-auto w-[165px] object-contain transition-transform duration-200 hover:scale-105 md:w-[230px]"
                    />
                </Link>

                <Link
                    href="/hazi-versenyek"
                    className="
                        mt-9 flex h-[48px] min-w-[282px] items-center justify-center
                        rounded-[6px] bg-[#f5a400] px-5
                        text-[18px] font-black text-black
                        transition-transform duration-200 ease-out
                        hover:scale-105
                        active:translate-y-[2px]
                        active:scale-95
                        md:h-[56px]
                        md:min-w-[340px]
                        md:text-[21px]
                    "
                >
                    {language === "en"
                        ? "house tournaments"
                        : "házi versenyek"}
                </Link>

                <MainMenu variant="footer" />

                <div className="
                    mt-10
                    flex
                    w-full
                    flex-col
                    items-center
                    justify-center
                    gap-6
                    md:mt-12
                    md:flex-row
                    md:items-start
                ">
                    <TripadvisorWidget
                        language={
                            language
                        }
                    />

                    <GoogleReviewsWidget
                        language={
                            language
                        }
                    />
                </div>

                <div className="mt-12 flex w-full flex-col items-center gap-6 md:mt-16 md:flex-row md:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="text-[10px] font-medium lowercase tracking-[0.04em] text-white/35 md:text-[11px]">
                            {language === "en"
                                ? "secure payment with"
                                : "biztonságos fizetés"}
                        </span>

                        <img
                            src="/v4/stripe_wordmark_blurple.svg"
                            alt="Stripe"
                            className="h-[22px] w-auto opacity-65 md:h-[24px]"
                        />
                    </div>

                    <p className="text-[10px] font-medium leading-relaxed text-white/30 md:text-[11px]">
                        {language === "en"
                            ? "© 2026 ÖTLET OUTLET KFT. All rights reserved."
                            : "© 2026 ÖTLET OUTLET KFT. Minden jog fenntartva."}
                    </p>
                </div>
            </div>
        </footer>
    );
}